import axios, { AxiosResponse, CancelTokenSource } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import useSWR, { SWRConfiguration } from "swr";
import { useLatest } from "./useLatest";

interface MutationResult<Data = any> {
  isRequesting: boolean;
  data: Data | undefined;
  error: any;
  status: number | undefined;
  timeoutError: boolean;
  cancel: () => void;
}

type Variables = Record<string, any>;

type FetchPolicyType = "cache-and-network" | "network-only";

export const useMutation = <Data = any, V = Variables>(
  inputKey: string,
  fetcher: (variables: Variables, options?: any) => Promise<AxiosResponse<any>>,
  fetchPolicy?: FetchPolicyType,
): [MutationResult<Data>, (variables?: V) => void] => {
  let [key, setKey] = useState<string | null>(null);
  let [cancelToken, setCancelToken] = useState<CancelTokenSource | undefined>(
    undefined,
  );
  let [variables, setVariables] = useState<Variables>({});

  let _fetcher = useCallback(() => {
    return fetcher(variables, { cancelToken: cancelToken?.token });
  }, [cancelToken?.token, fetcher, variables]);
  let [otherErrors, setOtherErrors] = useState(false);

  let defaultOptions: SWRConfiguration = {
    errorRetryCount: 0,
    refreshInterval: 0,
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };

  let {
    isValidating,
    data: result,
    error,
    mutate,
  } = useSWR(key, _fetcher, {
    ...defaultOptions,
    onError: (error, key) => {
      if (!error) {
        setOtherErrors(true);
      }
    },
  });

  let requestedRef = useRef(false);
  const doRequest = useCallback(
    (variables?: V) => {
      if (variables) {
        setVariables(variables);
      }
      if (fetchPolicy === "cache-and-network") {
        setCancelToken(axios.CancelToken.source());
        setKey(inputKey);
      } else {
        let random = Date.now();
        let key = inputKey + random;
        setCancelToken(axios.CancelToken.source());
        setKey(key);
      }
      setData(undefined);
      setStatus(undefined);
      setOtherErrors(false);
      requestedRef.current = true;
    },
    [fetchPolicy, inputKey],
  );

  let [data, setData] = useState<Data | undefined>(undefined);
  let [status, setStatus] = useState<number | undefined>(undefined);

  // do not revalidate after get data
  let resultRef = useLatest(result);
  let errorRef = useLatest(error);
  useEffect(() => {
    if (!requestedRef.current) return;
    if (isValidating) {
      return;
    }
    if (!resultRef.current && !errorRef.current) return;
    if (resultRef.current) {
      setData(resultRef.current.data);
      setStatus(resultRef.current.status);
    }
    setVariables({});
    setCancelToken(undefined);
    setKey(null);
  }, [errorRef, isValidating, resultRef]);

  const cancel = useCallback(() => {
    cancelToken?.cancel();
  }, [cancelToken]);

  return [
    {
      isRequesting: isValidating,
      data,
      error: error?.response?.data ? error.response.data : error,
      status,
      timeoutError: otherErrors && !error,
      cancel,
    },
    doRequest,
  ];
};
